import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { api } from '@/utils/api';
import { getImageUrl } from '@/utils/config';
import { EyeIcon } from '@/ui/icons';
import { ThumbnailImage } from '@/ui/ProgressiveImage';

type GalleryPost = {
  id: number;
  title: string;
  slug: string;
  coverImageUrl?: string;
  coverThumbUrl?: string;
  publishedAt?: string;
  viewCount?: number;
};

export function GalleryPage() {
  const [posts, setPosts] = useState<GalleryPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const PAGE_SIZE = 12;

  useEffect(() => {
    document.title = 'Galeri | ESDFL MUN';
    let ignore = false;
    (async () => {
      try {
        const res = await api.get(`/api/public/gallery?limit=${PAGE_SIZE}&offset=0`);
        if (!ignore) {
          setPosts(res.posts ?? []);
          setHasMore(!!res.hasMore);
          setOffset(res.posts?.length ?? 0);
        }
      } finally {
        if (!ignore) setLoading(false);
      }
    })();
    return () => { ignore = true; };
  }, []);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const res = await api.get(`/api/public/gallery?limit=${PAGE_SIZE}&offset=${offset}`);
      const newPosts: GalleryPost[] = res.posts ?? [];
      setPosts((p) => [...p, ...newPosts]);
      setOffset(offset + newPosts.length);
      setHasMore(!!res.hasMore);
    } finally {
      setLoadingMore(false);
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm transition-colors duration-300">
      <Helmet>
        <title>Galeri | ESDFL MUN</title>
        <meta name="description" content="ESDFL MUN galeri - etkinlik fotoğrafları" />
      </Helmet>

      <h1 className="text-2xl md:text-3xl font-serif font-bold mb-6 text-gray-900 dark:text-white">Galeri</h1>

      {loading && <div className="text-gray-600 dark:text-gray-400">Yükleniyor…</div>}
      
      {!loading && posts.length === 0 && (
        <div className="card p-8 text-center dark:bg-gray-700">
          <p className="text-gray-600 dark:text-gray-400">Henüz galeri içeriği yok.</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {posts.map((post) => (
          <Link 
            to={`/galeri/${post.slug}`} 
            key={post.id} 
            className="group rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-700 overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
          >
            {post.coverImageUrl ? (
              <ThumbnailImage 
                src={getImageUrl(post.coverThumbUrl || post.coverImageUrl)!} 
                alt={post.title} 
                className="aspect-[4/3] w-full group-hover:scale-[1.02] transition-transform duration-300" 
              />
            ) : (
              <div className="aspect-[4/3] w-full bg-gray-100 dark:bg-gray-600 flex items-center justify-center">
                <svg className="w-12 h-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            )}
            <div className="p-4">
              <h2 className="text-lg font-semibold mb-1 text-gray-900 dark:text-white group-hover:text-brand-600 dark:group-hover:text-brand-400 transition-colors">{post.title}</h2>
              <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
                {post.publishedAt && (
                  <span>{new Date(post.publishedAt).toLocaleDateString('tr-TR')}</span>
                )}
                <span className="inline-flex items-center gap-1"><EyeIcon /> {post.viewCount ?? 0}</span>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {hasMore && (
        <div className="flex justify-center mt-6">
          <button className="btn" onClick={loadMore} disabled={loadingMore}>
            {loadingMore ? 'Yükleniyor…' : 'Daha fazla göster'}
          </button>
        </div>
      )}
    </div>
  );
}
